import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Download, Pause, Play, Trash2, X } from 'lucide-react';

/**
 * The floating bar that appears once rows are selected.
 *
 * `framer-motion` rather than a CSS transition: it is already a dependency used in ten places,
 * including `directorCamp/ProgramForm/ui/SectionCard.js`, and `AnimatePresence` is what animates
 * the bar *out* — a plain transition cannot, because the element is unmounted the moment the
 * selection empties.
 *
 * `busy` carries a run in progress as `{ verb, done, total }`. Every button is disabled while it is
 * set, and the count is replaced by progress: a bulk run is N separate requests, so "Deactivating
 * 7 of 12…" is the difference between a bar that looks frozen and one that is visibly working.
 */
const BulkActionBar = ({ count, busy, onActivate, onDeactivate, onExport, onDelete, onClear }) => {
    const buttonClass =
        'cpn-inline-flex cpn-items-center cpn-gap-1.5 cpn-rounded-lg cpn-px-3 cpn-py-2 cpn-text-xs cpn-font-semibold cpn-text-neutral-200 cpn-transition hover:cpn-bg-white/10 hover:cpn-text-white disabled:cpn-cursor-not-allowed disabled:cpn-opacity-40 disabled:hover:cpn-bg-transparent disabled:hover:cpn-text-neutral-200';

    return (
        <AnimatePresence>
            {count > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 12 }}
                    transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                    className="cpn-fixed cpn-bottom-6 cpn-left-1/2 cpn-z-[65] cpn-flex cpn-w-[min(94vw,44rem)] -cpn-translate-x-1/2 cpn-flex-wrap cpn-items-center cpn-gap-2 cpn-rounded-2xl cpn-bg-neutral-900 cpn-px-4 cpn-py-3 cpn-shadow-pop"
                    role="region"
                    aria-label="Bulk actions"
                >
                    <p
                        className="cpn-m-0 cpn-mr-auto cpn-text-sm cpn-font-semibold cpn-text-white"
                        aria-live="polite"
                    >
                        {busy
                            ? `${busy.verb} ${busy.done} of ${busy.total}…`
                            : `${count} selected`}
                    </p>

                    <button type="button" onClick={onActivate} disabled={Boolean(busy)} className={buttonClass}>
                        <Play className="cpn-h-3.5 cpn-w-3.5" aria-hidden="true" />
                        Activate
                    </button>
                    <button type="button" onClick={onDeactivate} disabled={Boolean(busy)} className={buttonClass}>
                        <Pause className="cpn-h-3.5 cpn-w-3.5" aria-hidden="true" />
                        Deactivate
                    </button>
                    <button type="button" onClick={onExport} disabled={Boolean(busy)} className={buttonClass}>
                        <Download className="cpn-h-3.5 cpn-w-3.5" aria-hidden="true" />
                        Export
                    </button>
                    <button
                        type="button"
                        onClick={onDelete}
                        disabled={Boolean(busy)}
                        className="cpn-inline-flex cpn-items-center cpn-gap-1.5 cpn-rounded-lg cpn-bg-red-600 cpn-px-3 cpn-py-2 cpn-text-xs cpn-font-semibold cpn-text-white cpn-transition hover:cpn-bg-red-700 disabled:cpn-cursor-not-allowed disabled:cpn-opacity-40 disabled:hover:cpn-bg-red-600"
                    >
                        <Trash2 className="cpn-h-3.5 cpn-w-3.5" aria-hidden="true" />
                        Delete
                    </button>
                    <button
                        type="button"
                        onClick={onClear}
                        disabled={Boolean(busy)}
                        aria-label="Clear selection"
                        className="cpn-rounded-lg cpn-p-2 cpn-text-neutral-400 cpn-transition hover:cpn-bg-white/10 hover:cpn-text-white disabled:cpn-cursor-not-allowed disabled:cpn-opacity-40"
                    >
                        <X className="cpn-h-4 cpn-w-4" aria-hidden="true" />
                    </button>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default BulkActionBar;
